import {FormControl, FormGroup} from '@angular/forms';

import {BehaviorSubject, forkJoin} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    UserRelationTypesEnum,
    UsersService,
    ReportsService,
    UserReportInterface,
    UserResponseInterface,
    PortalModel,
    RegisterUserInputModel,
    UserReportModel,
    UserResponseModel,
    UserFilterModel,
    IndividualFilterModel,
    IndividualReportModel,
    ClientUserFilterModel,
    ClientUserReportModel
} from '@spa/api';

import { EmailExistsValidatorKey, InArrayValidator } from '@spa/validators';

import {FormComponent} from '../../../../shared/widgets';
import {Pagination} from '../../../../shared/components/pagination';
import { BaseControl, Helper, SelectControl } from '../../../../shared/classes';

import {ClientSearchResponseType} from '../../types';
import {ClientTypesEnum, UserDetailsFormErrorStatesEnum} from '../../enums';

import {UserDetailsControls, UserEmailSelector, UserPortalSelector} from '../../../../../assets/descriptors';

import {environment} from '../../../../../environments/environment';

export class UserDetailsForm {
    private readonly usersService: UsersService;
    private readonly reportsService: ReportsService;

    private portalClientName: string;
    private portals: PortalModel[];

    public form: FormGroup;
    public controls: BaseControl[];
    public controlsObservable: BehaviorSubject<BaseControl[]>;

    constructor(usersService: UsersService, reportsService: ReportsService) {
        this.usersService = usersService;
        this.reportsService = reportsService;

        this.controlsObservable = new BehaviorSubject([]);
    }

    private getClientSearchRequest(userId: number): ServerResponse<boolean> {
        const companyEmployeeFilterModel = this.getCompanyEmployeesFilter(userId);
        const individualFilterModel = this.getIndividualsFilter(userId);

        return forkJoin([
            this.reportsService.getClientUsersReport(companyEmployeeFilterModel),
            this.reportsService.getIndividualsReport(individualFilterModel)
        ]).pipe(
            map((responses: ServerResponseData<ClientSearchResponseType>[]) => {
                if (Helper.errorsHelper().checkForErrorResponses<ClientSearchResponseType>(responses)) {
                    return Helper.errorsHelper().createReducedErrorResponse<ClientSearchResponseType>(responses);
                }

                const filteredData = responses
                    .filter((response: ServerResponseData<ClientSearchResponseType>) => response.data !== null);

                return Helper.createSuccessResponse<boolean>(filteredData.length > 0);
            })
        );
    }

    private getCompanyEmployeesFilter(userId: number): ClientUserFilterModel {
        const itemsPerPagesChunk = Pagination.calculateItemsPerPagesChunk();

        return new ClientUserFilterModel(
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            userId,
            null,
            Pagination.defaultPagesChunk,
            itemsPerPagesChunk,
            ClientUserReportModel.getCustomerIdSelector(),
            false
        );
    }

    private getIndividualsFilter(userId: number): IndividualFilterModel {
        const itemsPerPagesChunk = Pagination.calculateItemsPerPagesChunk();

        return new IndividualFilterModel(
            null,
            null,
            null,
            null,
            null,
            userId,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            Pagination.defaultPagesChunk,
            itemsPerPagesChunk,
            IndividualReportModel.getEnrollDateSelector(),
            false,
            null
        );
    }

    private getUserIdIfNotAssigned(clientType: number, user: UserReportModel) {
        return this.getClientSearchRequest(user.correlationId).pipe(
            map((clientSearchResponse: ServerResponseData<boolean>) => {
                if (clientSearchResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<boolean>(
                        clientSearchResponse,
                        UserDetailsFormErrorStatesEnum.UserSearchFailed
                    );
                }

                const clientSearchResult = clientSearchResponse.data as boolean;

                const userRelationType = (clientType !== ClientTypesEnum.Individual) ? UserRelationTypesEnum.CompanyEmployee :
                    UserRelationTypesEnum.Individual;

                if (clientSearchResult || (user.userTypeRelation !== userRelationType)) {
                    this.controls
                        .filter((control: BaseControl) => control.name === UserEmailSelector)
                        .map((control: BaseControl) => {
                            const errorMessage =
                                FormComponent.getFormMessages(control.placeholder)[EmailExistsValidatorKey];

                            control.setError(errorMessage);
                        });

                    const error = new ApiError(UserDetailsFormErrorStatesEnum.UserExists);

                    return new ApiResponse(HttpStatusCodesEnum.BadRequest, [error]);
                }

                return Helper.createSuccessResponse<number>(user.correlationId);
            })
        );
    }

    private registerUser(clientType: number): ServerResponse<number> {
        const model = this.getUserInputModel(clientType);

        return this.usersService.register(model).pipe(
            map((response: ServerResponseData<UserResponseInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<UserResponseInterface>(
                        response,
                        UserDetailsFormErrorStatesEnum.UserCreationFailed
                    );
                }

                const data = response.data as UserResponseInterface;
                const userResponse = new UserResponseModel(data);

                return Helper.createSuccessResponse<number>(userResponse.id);
            })
        );
    }

    private getUserInputModel(clientType: number): RegisterUserInputModel {
        const redirectUrl = environment.authResetPasswordRedirectUrl + this.portalClientName;

        const filteredPortals = this.portals.find((portal: PortalModel) => portal.id === this.form.value[UserPortalSelector]);
        const portalName = filteredPortals ? filteredPortals.name : '';

        const userRelationType = (clientType !== ClientTypesEnum.Individual) ? UserRelationTypesEnum.CompanyEmployee :
            UserRelationTypesEnum.Individual;

        return new RegisterUserInputModel(
            this.form.value[UserEmailSelector],
            portalName,
            redirectUrl,
            userRelationType
        );
    }

    initForm(portals: PortalModel[]): void {
        this.form = new FormGroup({});
        this.controls = [];
        this.portals = portals;

        this.controls = UserDetailsControls().map((control: BaseControl) => {
            if (control.name === UserPortalSelector) {
                (control as SelectControl).options = portals.map((portal: PortalModel) => ({
                    label: portal.name,
                    value: portal.id
                }));

                const portalIds = portals.map((portal: PortalModel) => portal.id);

                control.extendValidators(InArrayValidator(portalIds));
            }

            this.form.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });

        this.controlsObservable.next(this.controls);
    }

    validateForm(getErrorMessage: (controlLabel?: string) => { [key: string]: string; }): boolean {
        if (!this.form.valid) {
            this.controls.map((control: BaseControl) => {
                if (!this.form.controls[control.name]) {
                    return false;
                }

                const formControl = this.form.controls[control.name];

                if (formControl.invalid) {
                    const errorKeys = Object.keys(formControl.errors);

                    const errors = errorKeys.map((key: string) => {
                        return getErrorMessage(control.placeholder)[key];
                    });

                    control.setErrors(errors);
                }
            });

            return false;
        }

        return true;
    }

    createRequest(clientType: number): ServerResponse<number> {
        this.portalClientName = Helper.enumsHelper().getClientNameByPortalId(
            Helper.enumsHelper().getDefaultTenantId(),
            this.form.value[UserPortalSelector]
        );

        const userFilter = new UserFilterModel(
            this.form.value[UserEmailSelector].toUpperCase(),
            this.portalClientName
        );

        return this.usersService.search(userFilter)
            .pipe(
                switchMap((searchResponse: ServerResponseData<UserReportInterface>) => {
                    if (searchResponse.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponseObservable<UserReportInterface>(
                            searchResponse,
                            UserDetailsFormErrorStatesEnum.UserSearchFailed
                        );
                    }

                    if (searchResponse.data) {
                        const model = new UserReportModel(searchResponse.data as UserReportInterface);

                        return this.getUserIdIfNotAssigned(clientType, model);
                    }

                    return this.registerUser(clientType);
                }));
    }
}
